import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2, Copy, Check, Loader2, Share2, Clock, Users } from 'lucide-react';
import { toast } from 'sonner';
import { fetchJson } from '@/lib/fetchJson';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Owner-side dialog: creates a time-limited public share link for a dashboard
 * or report via POST /api/share/preview and lets the user copy it.
 *
 * Props:
 *   open, onOpenChange
 *   resourceType: "report" | "dashboard"
 *   resourceId:   string
 *   resourceName: string  (used as default label)
 */
export function SharePreviewDialog({ open, onOpenChange, resourceType, resourceId, resourceName }) {
  const [expiresHours, setExpiresHours] = useState(168); // 7 days
  const [label, setLabel] = useState(resourceName || '');
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const resetOnClose = (v) => {
    if (!v) {
      setResult(null);
      setCopied(false);
      setLabel(resourceName || '');
      setExpiresHours(168);
    }
    onOpenChange(v);
  };

  const createLink = async () => {
    setCreating(true);
    try {
      const out = await fetchJson(`${API}/api/share/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resource_type: resourceType,
          resource_id: resourceId,
          expires_hours: Number(expiresHours) || 168,
          label,
        }),
      });
      // Build absolute URL for the share link (frontend origin + path from backend)
      const fullUrl = window.location.origin + out.share_url;
      setResult({ ...out, full_url: fullUrl });
    } catch (err) {
      toast.error(err.message || 'Could not create share link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async () => {
    if (!result?.full_url) return;
    try {
      await navigator.clipboard.writeText(result.full_url);
      setCopied(true);
      toast.success('Share link copied');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error('Could not copy — select the link manually');
    }
  };

  return (
    <Dialog open={open} onOpenChange={resetOnClose}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="share-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Share2 className="w-4 h-4 text-primary" />Share preview</DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-3 mt-2">
            <p className="text-xs text-muted-foreground">
              Create a time-limited public link that anyone can view without signing in.
              Viewers can explore the dashboard — downloading a PDF triggers a 14-day trial CTA,
              turning every share into a qualified lead.
            </p>
            <div>
              <Label className="text-xs text-muted-foreground">Label (optional — shown to viewers)</Label>
              <Input value={label} onChange={e => setLabel(e.target.value)} placeholder={resourceName} className="rounded-lg bg-muted/50 border-border/50 mt-1" data-testid="share-label-input" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" />Expires in</Label>
              <select
                value={expiresHours}
                onChange={e => setExpiresHours(Number(e.target.value))}
                className="w-full mt-1 rounded-lg bg-muted/50 border border-border/50 px-3 py-2 text-xs"
                data-testid="share-expiry-select"
              >
                <option value={24}>24 hours</option>
                <option value={72}>3 days</option>
                <option value={168}>7 days</option>
                <option value={720}>30 days</option>
              </select>
            </div>
            <Button onClick={createLink} disabled={creating} className="w-full rounded-lg bg-primary text-primary-foreground active:scale-95" data-testid="create-share-link-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />} Create share link
            </Button>
          </div>
        ) : (
          <div className="space-y-3 mt-2" data-testid="share-link-result">
            <div className="p-3 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
              <p className="text-[10px] font-heading font-semibold text-emerald-400 mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" /> Public share link
              </p>
              <div className="flex items-center gap-2">
                <code className="flex-1 min-w-0 text-[10px] font-mono text-foreground bg-background/60 border border-border/30 rounded px-2 py-1.5 truncate" data-testid="share-link-url">
                  {result.full_url}
                </code>
                <Button size="sm" variant="outline" onClick={copyLink} className="rounded-lg h-7 px-2 flex-shrink-0" data-testid="copy-share-link-btn">
                  {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Expires {new Date(result.expires_at).toLocaleString()}
              </p>
            </div>
            <div className="text-[10px] text-muted-foreground/80 px-1 leading-relaxed">
              Viewers see a read-only preview. If they try to download a PDF they'll be prompted to start a 14-day trial — each signup becomes a lead for your team.
            </div>
            <Button variant="outline" onClick={() => setResult(null)} className="w-full rounded-lg" data-testid="share-create-another-btn">Create another link</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
